import {
	DndContext,
	closestCenter
} from "@dnd-kit/core";
import {
	SortableContext,
	arrayMove,
	useSortable,
	verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DeleteOutlineOutlined, DragHandleOutlined, EditOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui/icons-material";
import {
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography
} from "@mui/material";
import { generateClient } from "aws-amplify/api";
import { Formik } from "formik";
import _ from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { array, object, string } from 'yup';
import { ConfirmDialog } from "../../components/confirmDialog/confirmDialog";
import ActivityLogSuppression from "../../components/engagement/suppression/activityLogSuppression";
import ContactFieldSuppressionLists from "../../components/engagement/suppression/contactFieldSuppressionLists";
import DNCLists from "../../components/engagement/suppression/dncLists";
import DNCSuppression from "../../components/engagement/suppression/dncSuppression";
import PocSuppression from "../../components/engagement/suppression/pocSuppression";
import RestService from '../../components/engagement/suppression/restService';
import Tester from "../../components/engagement/suppression/tester";
import { PageAppBar } from "../../components/pageAppBar";
import { UserContext } from '../../contexts/UserContext';
import { createSuppression, updateSuppression } from '../../graphql/mutations';
import { getSuppression, getTenantSettings, listSuppressions } from '../../graphql/queries';
import { SuppressionDefaults as SuppressionDefaultsSource } from "../../models/suppressionResults";
import { actionOneButtonStyle, actionTwoButtonStyle, cardStyle } from "../../theme";

/**
 * NOTE:
 * We re-use the same SuppressionDefaults you provided, but to avoid an import mismatch
 * and make this component standalone, we'll re-declare a local mapping that maps the UI label
 * to the dynamo value. If you already import SuppressionDefaults from models, you can
 * replace SuppressionDefaultsLocal with that import. For now, I'll read from your provided
 * constants variable name as the source if present; else fallback to a local copy.
 */
const SuppressionDefaults = (typeof SuppressionDefaultsSource !== 'undefined' && Array.isArray(SuppressionDefaultsSource) && SuppressionDefaultsSource.length > 0)
	? SuppressionDefaultsSource
	: [
		{ display: 'Suppression Final', dynamo: 'SuppressionFinal', code: '727' },
		{ display: 'Suppression Retry - Non Increment', dynamo: 'SuppressionRetryNonIncrement', code: '729' },
		{ display: 'Suppression Retry - Increment', dynamo: 'SuppressionRetryIncrement', code: '728' },
	];

/**
 * Detect the active suppression type in a `check` object.
 * Returns the key name (e.g. 'pocQuerySuppression', 'contactFieldSuppressions', ...)
 */
function detectSuppressionType(check) {
	// guard
	if (!check || typeof check !== 'object') return null;

	for (const key of Object.keys(check)) {
		if (check[key] != null) {
			return key;
		}
	}

	return null;
}

/**
 * Returns a user-friendly label for the suppression type
 */
const SUPPRESSION_LABELS = {
	activityLogQuerySuppression: "Activity Log Query",
	pocQuerySuppression: "POC Suppression",
	restSuppression: "REST Suppression",
	dncDotComSuppression: "DNC.com Suppression",
	dncListSuppressions: "DNC List Suppression",
	contactFieldSuppressions: "Contact Field Suppression"
};

/**
 * Factory - create a new check object with only the specified suppression type populated.
 * The "result" param should be a dynamo value, e.g. "SuppressionFinal"
 */
function createSuppressionCheck(typeKey, resultDynamo) {
	return {
		activityLogQuerySuppression: typeKey === "activityLogQuerySuppression" ? {
			query: "",
			rawQuery: "",
			threshold: 1,
			result: resultDynamo
		} : null,
		pocQuerySuppression: typeKey === "pocQuerySuppression" ? {
			query: "",
			rawQuery: "",
			suppressIfWeekendAndCantCallOnWeekends: false,
			result: resultDynamo
		} : null,
		restSuppression: typeKey === "restSuppression" ? {
			endpoint: "",
			method: "POST",
			params: [],
			headers: [],
			auth: { type: "None", info: "{}" },
			response: { path: "", type: "Boolean", value: "{\"value\":true}", options: "{\"responseStringTransformation\":\"ToUpper\"}" },
			suppressOnError: false,
			dispositionOnError: "",
			result: resultDynamo
		} : null,
		dncDotComSuppression: typeKey === "dncDotComSuppression" ? {
			scrub: false,
			state: false,
			numberReassignment: false,
			reassignmentSince: null,
			knownLitigator: false,
			result: resultDynamo
		} : null,
		dncListSuppressions: typeKey === "dncListSuppressions" ? {
			dncs: [],
			result: resultDynamo
		} : null,
		contactFieldSuppressions: typeKey === "contactFieldSuppressions" ? {
			contactFieldSuppressions: [],
			result: resultDynamo
		} : null
	};
}

/**
 * Reusable GetRow component that renders a summary row and an expandable
 * row containing the appropriate suppression configuration component.
 */
function GetRow({
	index,
	check,
	openRow,
	setOpenRow,
	formikProps,
	userContext,
	dncDotComSettings,
	logs,
	setLogs,
	onEdit,
	dragHandleProps,
	rowRef,
	rowStyle
}) {
	const isOpen = openRow === index;
	const suppressionType = detectSuppressionType(check);
	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
	const [deleteIndex, setDeleteIndex] = useState(null);

	const SuppressionComponentMap = {
		activityLogQuerySuppression: ActivityLogSuppression,
		pocQuerySuppression: PocSuppression,
		restSuppression: RestService,
		dncDotComSuppression: DNCSuppression,
		dncListSuppressions: DNCLists,
		contactFieldSuppressions: ContactFieldSuppressionLists
	};

	const SuppressionComponent = suppressionType ? SuppressionComponentMap[suppressionType] : null;
	const suppressionName = suppressionType ? SUPPRESSION_LABELS[suppressionType] : "Unknown";

	// Determine a user-friendly "result" label for the summary column
	let resultDisplay = "—";
	const val = suppressionType ? check[suppressionType] : null;
	if (val != null) {
		if (Array.isArray(val)) {
			resultDisplay = `${val.length} item${val.length === 1 ? "" : "s"}`;
		} else if (typeof val === "object") {
			const resultKey = val.result ?? val.resultType ?? null;
			if (resultKey) {
				resultDisplay = SuppressionDefaults.find(r => r.dynamo === resultKey)?.display || String(resultKey);
			} else {
				if (val.query) resultDisplay = "Has query";
				else resultDisplay = "Configured";
			}
		} else {
			resultDisplay = String(val);
		}
	}

	return (
		<>
			<TableRow
				ref={rowRef}
				style={rowStyle}
				hover
				sx={{ cursor: "pointer" }}
			>
				<TableCell sx={{ width: 40 }} onClick={() => setOpenRow(isOpen ? null : index)}>
					{isOpen ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
				</TableCell>
				<TableCell onClick={() => setOpenRow(isOpen ? null : index)}>{suppressionName}</TableCell>
				<TableCell align="center" onClick={() => setOpenRow(isOpen ? null : index)}>{resultDisplay}</TableCell>
				<TableCell align="center" onClick={() => setOpenRow(isOpen ? null : index)}>{index + 1}</TableCell>
				<TableCell align="center">
					<IconButton
						color="primary"
						onClick={() => onEdit(index)}
					>
						<EditOutlined fontSize="small" />
					</IconButton>
					<IconButton
						color="error"
						onClick={() => {
							setConfirmDeleteOpen(true);
							setDeleteIndex(index);
						}}
					>
						<DeleteOutlineOutlined fontSize="small" />
					</IconButton>
					<IconButton
						color="primary" {...dragHandleProps} sx={{ cursor: "grab" }} >
						<DragHandleOutlined fontSize="small" />
					</IconButton>
				</TableCell>
			</TableRow>

			{/* The collapse row ensures smooth animation and keeps layout stable */}
			<TableRow>
				<TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
					<Collapse in={isOpen} timeout="auto" unmountOnExit>
						<Box sx={{
							padding: 5
						}}>
							{SuppressionComponent ? (
								<SuppressionComponent
									formikProps={formikProps}
									tenant={userContext?.tenantId}
									config={dncDotComSettings}
									logs={logs}
									setLogs={setLogs}
									errors={formikProps.errors}
									touched={formikProps.touched}
									checkIndex={index} // pass index so child components can update nested values if needed
								/>
							) : (
								<Typography variant="body2" color="text.secondary">
									No configuration component available for this suppression type.
								</Typography>
							)}
						</Box>
					</Collapse>
				</TableCell>
			</TableRow>

			<ConfirmDialog
				open={confirmDeleteOpen}
				actionTwoHandler={() => setConfirmDeleteOpen(false)}
				actionOneHandler={() => {
					if (deleteIndex != null) {
						const next = (formikProps.values.checks || []).slice();
						next.splice(deleteIndex, 1);
						formikProps.setFieldValue("checks", next);
						// close if the deleted row was open
						if (openRow === deleteIndex) setOpenRow(null);
					}
					setConfirmDeleteOpen(false);
				}}
				title="Delete Suppression Check"
				description="Are you sure you want to delete this suppression check?"
				actionOneText="Delete"
				actionTwoText="Cancel"
			/>
		</>
	);
}

function SortableGetRow(props) {
	const { index } = props;

	const {
		setNodeRef,
		attributes,
		listeners,
		transform
	} = useSortable({
		id: index,
		transition: null
	});

	const style = {
		transform: CSS.Transform.toString(transform),
		transition: undefined
	};

	return (
		<GetRow
			{...props}
			rowRef={setNodeRef}
			rowStyle={style}
			dragHandleProps={{ ...attributes, ...listeners }}
		/>
	);
}

/**
 * Add/Edit Check Modal component
 * - mode: "add" | "edit"
 * - index: used only for edit (can be null for add)
 * - initialType / initialResult: values to pre-populate when editing
 * - onSave(index, selectedType, selectedResult)
 */
function AddCheckModal({
	open,
	mode = "add",
	index = null,
	initialType,
	initialResult,
	onClose,
	onSave
}) {
	const [selectedType, setSelectedType] = useState("activityLogQuerySuppression");
	const [selectedResult, setSelectedResult] = useState(SuppressionDefaults[0].dynamo);

	useEffect(() => {
		if (open) {
			setSelectedType(initialType ?? "activityLogQuerySuppression");
			setSelectedResult(initialResult ?? SuppressionDefaults[0].dynamo);
		}
	}, [open, initialType, initialResult]);

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>
				{mode === "add" ? "Add Suppression Check" : "Edit Suppression Check"}
			</DialogTitle>

			<DialogContent>
				<Box sx={{ mt: 1 }}>

					{/* TYPE SELECT */}
					<FormControl fullWidth margin="normal" variant="outlined">
						<InputLabel id="suppression-type-label">Suppression Type</InputLabel>
						<Select
							labelId="suppression-type-label"
							id="suppression-type"
							value={selectedType}
							label="Suppression Type"
							onChange={(e) => setSelectedType(e.target.value)}
						>
							{Object.entries(SUPPRESSION_LABELS).map(([key, label]) => (
								<MenuItem key={key} value={key}>{label}</MenuItem>
							))}
						</Select>
					</FormControl>

					{/* RESULT SELECT */}
					<FormControl fullWidth margin="normal" variant="outlined">
						<InputLabel id="suppression-result-label">Result</InputLabel>
						<Select
							labelId="suppression-result-label"
							id="suppression-result"
							value={selectedResult}
							label="Result"
							onChange={(e) => setSelectedResult(e.target.value)}
						>
							{SuppressionDefaults.map(sd => (
								<MenuItem key={sd.dynamo} value={sd.dynamo}>
									{sd.display}
								</MenuItem>
							))}
						</Select>
					</FormControl>

				</Box>
			</DialogContent>

			<DialogActions>
				<Button sx={actionTwoButtonStyle} onClick={onClose}>
					Cancel
				</Button>

				<Button
					sx={actionOneButtonStyle}
					variant="contained"
					onClick={() => {
						onSave(index, selectedType, selectedResult);
						onClose();
					}}
				>
					{mode === "add" ? "Create" : "Save"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}


export default function Suppression() {
	const client = generateClient();
	const { suppressionId } = useParams();
	const navigate = useNavigate();
	const userContext = useContext(UserContext);
	const [suppressionObj, setSuppressionObj] = useState({
		name: '',
		checks: []
	});
	const [invalidId, setInvalidId] = useState(false);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [currentTab, setCurrentTab] = useState(0);
	const [logs, setLogs] = useState([]);
	const [dncDotComSettings, setDncDotComSettings] = useState({ orgId: '', projectId: '', loginId: '', scrubEnabled: false, reassignmentEnabled: false, knownLitigatorEnabled: false });
	const isNew = useRef(false);
	const [navigateConfirmOpen, setNavigateConfirmOpen] = useState(false);

	// local state for which row is open (exclusive)
	const [openRow, setOpenRow] = useState(null);

	// Add-check modal (for creating new check)
	const [addModalOpen, setAddModalOpen] = useState(false);

	// Edit modal state
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [editIndex, setEditIndex] = useState(null);
	const [editInitialType, setEditInitialType] = useState(null);
	const [editInitialResult, setEditInitialResult] = useState(null);

	useEffect(() => {
		async function getData() {
			setLoading(true);

			if (suppressionId === "new") {
				isNew.current = true;
				await getDncDotComSettings();
			} else if (!isNew.current) {
				const dncDotComSettings = await getDncDotComSettings();
				isNew.current = false;

				const result = await client.graphql({
					query: getSuppression,
					variables: { id: suppressionId },
				});

				if (result?.data?.getSuppression) {
					const suppressionData = result.data.getSuppression;
					// ensure checks exists as an array for Formik initialValues
					// Loop through checks to parse any JSON strings back into objects
					if (Array.isArray(suppressionData.checks)) {
						for (const check of suppressionData.checks) {
							const typeKey = detectSuppressionType(check);
							if (!typeKey) continue;
							const typed = check[typeKey];
							if (!typed) continue;
							// activity log & poc may have query/rawQuery fields that need parsing
							if (typeKey === "activityLogQuerySuppression") {
								try {
									if (typed.rawQuery && typeof typed.rawQuery === 'string') typed.rawQuery = JSON.parse(typed.rawQuery);
								} catch (e) { }
								try {
									if (typed.query && typeof typed.query === 'string') typed.query = JSON.parse(typed.query);
								} catch (e) { }
							}
							if (typeKey === "pocQuerySuppression") {
								try {
									if (typed.rawQuery && typeof typed.rawQuery === 'string') typed.rawQuery = JSON.parse(typed.rawQuery);
								} catch (e) { }
								try {
									if (typed.query && typeof typed.query === 'string') typed.query = JSON.parse(typed.query);
								} catch (e) { }
							}
							// rest suppression needs some nested parsing
							if (typeKey === "restSuppression") {
								typed.auth = typed.auth || {};
								try {
									if (typed.auth.info && typeof typed.auth.info === 'string') typed.auth.info = JSON.parse(typed.auth.info);
								}
								catch (e) { }
								try {
									if (typed.response && typed.response.value && typeof typed.response.value === 'string') typed.response.value = JSON.parse(typed.response.value);
								} catch (e) { }
								try {
									if (typed.response && typed.response.options && typeof typed.response.options === 'string') typed.response.options = JSON.parse(typed.response.options);
								} catch (e) { }
							}
						}
					}

					setSuppressionObj({ ...suppressionData, checks: Array.isArray(suppressionData.checks) ? suppressionData.checks : [] });
				}
			}

			setLoading(false);
		}

		if (userContext.tenantId && suppressionId) {
			getData();
		}
	}, [userContext, suppressionId]);



	async function getDncDotComSettings() {
		const tenantSettings = await client.graphql({
			query: getTenantSettings,
			variables: { id: userContext.tenantId },
		});

		const s = {
			orgId: tenantSettings?.data?.getTenantSettings?.dncDotComSettings?.orgId || '',
			projectId: tenantSettings?.data?.getTenantSettings?.dncDotComSettings?.projectId || '',
			loginId: tenantSettings?.data?.getTenantSettings?.dncDotComSettings?.loginId || '',
			scrubEnabled: tenantSettings?.data?.getTenantSettings?.dncDotComSettings?.scrubEnabled || false,
			reassignmentEnabled: tenantSettings?.data?.getTenantSettings?.dncDotComSettings?.reassignmentEnabled || false,
			knownLitigatorEnabled: tenantSettings?.data?.getTenantSettings?.dncDotComSettings?.knownLitigatorEnabled || false
		};

		setDncDotComSettings(s);
		return s;
	}

	return (
		<Formik
			initialValues={suppressionObj}
			enableReinitialize
			validationSchema={object().shape({
				name: string().required("A name is required"),
				checks: array()
			})}
			onSubmit={async values => {
				setSaving(true);

				const duplicateName = await client.graphql({
					query: listSuppressions,
					variables: {
						filter: {
							name: {
								eq: values.name
							}
						}
					}
				});


				if (duplicateName?.data?.listSuppressions?.items?.some(x => x.id !== suppressionId)) {
					enqueueSnackbar(`A suppression profile with the name '${values.name}' already exists.`, { variant: 'error' });
				} else {
					let mutation = updateSuppression;

					if (suppressionId === "new") {
						mutation = createSuppression;
					}

					const clone = _.cloneDeep(values);
					clone.updatedBy = userContext.email;

					delete clone.createdAt;
					delete clone.updatedAt;

					// Safely stringify nested query fields if present inside checks[]
					if (Array.isArray(clone.checks)) {
						for (const c of clone.checks) {
							// each check has one non-null key; find it
							const typeKey = detectSuppressionType(c);
							if (!typeKey) continue;
							const typed = c[typeKey];

							if (!typed) continue;

							// activity log & poc may have query/rawQuery fields that need stringifying
							if (typeKey === "activityLogQuerySuppression" || typeKey === "pocQuerySuppression") {
								try {
									if (typed.rawQuery && typeof typed.rawQuery !== 'string') typed.rawQuery = JSON.stringify(typed.rawQuery);
								} catch (e) { }
								try {
									if (typed.query && typeof typed.query !== 'string') typed.query = JSON.stringify(typed.query);
								} catch (e) { }
							}

							// rest suppression needs some nested stringification
							if (typeKey === "restSuppression") {
								typed.auth = typed.auth || {};
								try {
									if (typed.auth.info && typeof typed.auth.info !== 'string') typed.auth.info = JSON.stringify(typed.auth.info);
								} catch (e) { }
								try {
									if (typed.response && typed.response.value && typeof typed.response.value !== 'string') typed.response.value = JSON.stringify(typed.response.value);
								} catch (e) { }
								try {
									if (typed.response && typed.response.options && typeof typed.response.options !== 'string') typed.response.options = JSON.stringify(typed.response.options);
								} catch (e) { }
							}
						}
					}

					clone.tenant = userContext.tenantId;
					try {
						const result = await client.graphql({
							query: mutation,
							variables: { input: clone },
						});
						if (suppressionId === "new") {
							values.id = result.data.createSuppression.id;
							navigate(`/suppression/${result.data.createSuppression.id}`);
						}
						setSuppressionObj({ ...values });
						enqueueSnackbar("Suppression saved successfully", { variant: 'success' });
					} catch (err) {
						console.log(err);
						enqueueSnackbar("Suppression could not be saved", { variant: 'error' });
					}
				}

				setSaving(false);
			}}
		>
			{formikProps => {
				// helper to add a check to formik values
				const addCheckToFormik = (typeKey, resultDynamo) => {
					const newCheck = createSuppressionCheck(typeKey, resultDynamo);
					const next = Array.isArray(formikProps.values.checks) ? [...formikProps.values.checks, newCheck] : [newCheck];
					formikProps.setFieldValue("checks", next);
					// open the newly added row for editing
					setTimeout(() => {
						setOpenRow(next.length - 1);
						// small scroll to bottom
						window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
					}, 100);
				};

				// Save edited check back into Formik at provided index
				const saveEditedCheck = (index, typeKey, resultDynamo) => {
					const next = Array.isArray(formikProps.values.checks)
						? _.cloneDeep(formikProps.values.checks)
						: [];

					const existingCheck = next[index];
					if (!existingCheck) return;

					const existingType = detectSuppressionType(existingCheck);
					const existingTyped = existingType ? existingCheck[existingType] : null;

					if (existingType === typeKey && existingTyped) {
						const resultKey = "result" in existingTyped ? "result" : "resultType";

						next[index] = {
							...existingCheck,
							[typeKey]: {
								...existingTyped,
								[resultKey]: resultDynamo
							}
						};
					} else {
						next[index] = createSuppressionCheck(typeKey, resultDynamo);
					}

					formikProps.setFieldValue("checks", next);

					// reopen & scroll
					setTimeout(() => {
						setOpenRow(index);
						const el = document.querySelectorAll('tr')[index * 2];
						if (el && el.scrollIntoView) {
							el.scrollIntoView({ behavior: 'smooth', block: 'center' });
						}
					}, 150);
				};

				// Handler passed to modal - adds or updates depending on index param
				const handleSaveFromModal = (index, selectedType, selectedResult) => {
					if (index == null) {
						addCheckToFormik(selectedType, selectedResult);
					} else {
						saveEditedCheck(index, selectedType, selectedResult);
					}
				};

				// Called by GetRow when user clicks Edit - open modal pre-filled
				const editRow = (index) => {
					const checks = Array.isArray(formikProps.values.checks) ? formikProps.values.checks : [];
					if (!checks[index]) return;

					const check = checks[index];
					const type = detectSuppressionType(check);
					const typed = type ? check[type] : null;
					const result = typed?.result ?? typed?.resultType ?? SuppressionDefaults[0].dynamo;

					// set modal state
					setEditIndex(index);
					setEditInitialType(type ?? "activityLogQuerySuppression");
					setEditInitialResult(result);
					setEditModalOpen(true);

					// also ensure row is visible if desired (collapse handled after save)
					setTimeout(() => {
						const el = document.querySelectorAll('tr')[index * 2];
						if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
					}, 150);
				};

				const handleDragEnd = (event) => {
					const { active, over } = event;

					// dropped outside the list
					if (!over || active.id === over.id) return;

					const oldIndex = active.id;
					const newIndex = over.id;

					const next = arrayMove(
						formikProps.values.checks,
						oldIndex,
						newIndex
					);

					formikProps.setFieldValue("checks", next);

					// Keep the correct row expanded after reorder
					if (openRow === oldIndex) {
						setOpenRow(newIndex);
					} else if (
						openRow !== null &&
						oldIndex < openRow &&
						newIndex >= openRow
					) {
						setOpenRow(openRow - 1);
					} else if (
						openRow !== null &&
						oldIndex > openRow &&
						newIndex <= openRow
					) {
						setOpenRow(openRow + 1);
					}
				};

				return (
					<div>
						<form onSubmit={formikProps.handleSubmit}>
							<Box display='flex' flexDirection='column' gap={2}>
								<PageAppBar
									title="Suppression Profile"
									actionOneText="Save"
									actionOneHandler={formikProps.handleSubmit}
									actionOneDisabled={userContext.securityOptions.suppression < 2}
									actionTwoText="Close"
									actionTwoHandler={() => {
										if (formikProps.dirty) {
											setNavigateConfirmOpen(true);
										} else {
											navigate(-1)
										}
									}}
									actionTwoDisabled={saving || loading}
								/>

								<Box display="grid" gridTemplateColumns="1fr">
									<TextField
										required
										label='Name'
										fullWidth
										value={formikProps.values.name}
										name='name'
										onChange={formikProps.handleChange}
										onBlur={formikProps.handleBlur}
										error={formikProps.errors.name && formikProps.touched.name}
										helperText={formikProps.touched.name && formikProps.errors.name} />
								</Box>

								<Card variant='outlined' sx={cardStyle}>
									<CardContent>
										<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
											<Typography variant="h6">Suppression Checks</Typography>
											<Box>
												<Button
													sx={actionOneButtonStyle}
													onClick={() => setAddModalOpen(true)}
													disabled={userContext.securityOptions.suppression < 2 || saving || loading}
												>
													Add Check
												</Button>
											</Box>
										</Box>

										{!loading && (
											<DndContext
												collisionDetection={closestCenter}
												onDragEnd={handleDragEnd}
											>
												<SortableContext
													items={(formikProps.values.checks || []).map((_, i) => i)}
													strategy={verticalListSortingStrategy}
												>
													<Table>
														<TableHead>
															<TableRow>
																<TableCell />
																<TableCell>Suppression Check</TableCell>
																<TableCell align="center">Result</TableCell>
																<TableCell align="center">Order</TableCell>
																<TableCell align="center">Actions</TableCell>
															</TableRow>
														</TableHead>

														<TableBody>
															{(formikProps.values.checks || []).map((check, index) => (
																<SortableGetRow
																	key={index}
																	index={index}
																	check={check}
																	openRow={openRow}
																	setOpenRow={setOpenRow}
																	formikProps={formikProps}
																	userContext={userContext}
																	dncDotComSettings={dncDotComSettings}
																	logs={logs}
																	setLogs={setLogs}
																	onEdit={editRow}
																/>
															))}
														</TableBody>
													</Table>
												</SortableContext>
											</DndContext>
										)}

										{loading && <CircularProgress />}
									</CardContent>
								</Card>

								<Box>
									<Tester formikProps={formikProps} logs={logs} setLogs={setLogs} dncDotComSettings={dncDotComSettings} />
								</Box>
							</Box>

							<ConfirmDialog
								open={navigateConfirmOpen}
								actionTwoHandler={() => setNavigateConfirmOpen(false)}
								actionOneHandler={() => {
									setNavigateConfirmOpen(false);
									navigate(-1);
								}}
								title="Discard Changes"
								description="Are you sure you want to discard your changes?"
								actionOneText="Discard"
								actionTwoText="Cancel"
							/>

							{/* Add modal (create) */}
							<AddCheckModal
								open={addModalOpen}
								mode="add"
								index={null}
								initialType={null}
								initialResult={null}
								onClose={() => setAddModalOpen(false)}
								onSave={(indexParam, type, result) => {
									// indexParam will be null for add — delegate into existing helper
									if (indexParam == null) addCheckToFormik(type, result);
									else saveEditedCheck(indexParam, type, result); // defensive
								}}
							/>

							{/* Edit modal (edit existing) */}
							<AddCheckModal
								open={editModalOpen}
								mode="edit"
								index={editIndex}
								initialType={editInitialType}
								initialResult={editInitialResult}
								onClose={() => setEditModalOpen(false)}
								onSave={(indexParam, type, result) => {
									// indexParam should be editIndex
									const targetIndex = typeof indexParam === 'number' ? indexParam : editIndex;
									if (typeof targetIndex === 'number') saveEditedCheck(targetIndex, type, result);
								}}
							/>
						</form>
					</div>
				);
			}}
		</Formik>
	);
}
